/** @format */

import React, { useState, useRef, useContext, useEffect } from "react";
import { Button, Modal, Form, Input, Alert, Select, Spin } from "antd";
import VoiceGainAuthSvcApi from "../../../apis/VoiceGainAuthSvcApi";
import ReCAPTCHA from "react-google-recaptcha";
import { IReCaptchaProps } from "../../../components/ReCaptcha/ReCaptcha.types";
import { IRegistrationFormProps } from "../../RegistrationContent/RegistrationForm/RegistrationForm.types";
import ReCaptcha from "../../../components/ReCaptcha";
import { AccountsOverviewContainerContext } from "./AccountsOverview.container";
import { PlusOutlined } from "@ant-design/icons";

function isValidEmail(email: string): boolean {
  var re =
    /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return re.test(email);
}

interface IAccountsOverviewAddAccountModalProps {
  sitekey: string;
  onSuccess: () => void;
}

const AccountsOverviewAddAccountModal: React.FC<
  IAccountsOverviewAddAccountModalProps
> = ({ sitekey }) => {
  const accountsOverviewContainerContext = useContext(
    AccountsOverviewContainerContext
  );
  const [emailIsInvalid, setEmailIsInvalid] = useState<boolean>(false);
  const [isAddModalVisible, setIsAddModalVisible] = useState(false);
  const [firstName, setFirstName] = useState<string>("");
  const [lastName, setLastName] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [accountType, setAccountType] = useState<string | undefined>(
    "SPEECH-WORKS"
  );
  const [businessName, setBusinessName] = useState<string | undefined>(
    undefined
  );
  const [webName, setWebName] = useState<string | undefined>(undefined);
  const [accountCreationStatus, setAccountCreationStatus] =
    useState<string>("");
  const [reCaptchaToken, setReCaptchaToken] = useState<string>("");
  const [isPendingAdd, setIsPendingAdd] = useState<boolean>(false);
  const [firstNameSpecialCharacterError, setFirstNameSpecialCharacterError] =
    useState<string>("");
  const [lastNameSpecialCharacterError, setLastNameSpecialCharacterError] =
    useState<string>("");
  const [isReadOnly, setIsReadOnly] = useState<boolean>(false);

  const reCaptchaRef = useRef<ReCAPTCHA>();

  const generateReCaptchaProps = (): IReCaptchaProps => ({
    onExpire: () => {
      setReCaptchaToken("");
    },
    onSuccess: (token) => {
      setReCaptchaToken(token);
    },
    reCaptchaRef: reCaptchaRef,
    sitekey,
  });

  //Modal controls
  const showAddModal = () => {
    setIsAddModalVisible(true);
  };

  useEffect(() => {
    setIsReadOnly(false);
  }, [isAddModalVisible]);

  const handleAddModalOk = () => {
    if (!isReadOnly && !emailIsInvalid) {
      const emailValid = isValidEmail(email);
      if (emailValid) {
        setIsPendingAdd(true);
        VoiceGainAuthSvcApi.addNewAccount(
          firstName,
          lastName,
          email,
          accountType,
          businessName,
          webName,
          reCaptchaToken
        ).then((response) => {
          if (response.ok) {
            setAccountCreationStatus("success");
            setIsReadOnly(true);
            setIsPendingAdd(false);
            if (accountsOverviewContainerContext.mutateAccounts)
              accountsOverviewContainerContext.mutateAccounts();
            if (reCaptchaRef.current !== undefined) {
              reCaptchaRef.current.reset();
            }
          } else {
            setIsPendingAdd(false);
            setAccountCreationStatus("error");
            if (reCaptchaRef.current !== undefined) {
              reCaptchaRef.current.reset();
            }
            throw new Error("Error creating account");
          }
        });
      } else {
        setEmailIsInvalid(true);
      }
    }
  };

  const handleAddModalCancel = () => {
    setIsAddModalVisible(false);
    setFirstName("");
    setLastName("");
    setEmail("");
    setFirstNameSpecialCharacterError("");
    setLastNameSpecialCharacterError("");
    setBusinessName(undefined);
    setWebName(undefined);
    setAccountCreationStatus("");
    setEmailIsInvalid(false);
    if (reCaptchaRef.current !== undefined) {
      reCaptchaRef.current.reset();
    }
  };

  const handleDisableAddBtn = () => {
    if (
      !emailIsInvalid &&
      !isReadOnly &&
      firstName !== "" &&
      lastName !== "" &&
      email !== "" &&
      accountType !== undefined &&
      accountType !== "" &&
      reCaptchaToken !== undefined &&
      reCaptchaToken !== "" &&
      firstNameSpecialCharacterError !== "error" &&
      lastNameSpecialCharacterError !== "error"
    )
      return false;
    else return true;
  };

  const handleFirstNameSpecialCharErr = () => {
    if (firstNameSpecialCharacterError === "error") {
      return "error";
    } else if (firstNameSpecialCharacterError === "") {
      return "";
    }
  };

  const handleFirstNameSpecialCharErrMsg = () => {
    if (firstNameSpecialCharacterError === "error") {
      return "No special chracters allowed.";
    } else if (firstNameSpecialCharacterError === "") {
      return "";
    }
  };

  const handleLastNameSpecialCharErr = () => {
    if (lastNameSpecialCharacterError === "error") {
      return "error";
    } else if (lastNameSpecialCharacterError === "") {
      return "";
    }
  };

  const handleLastNameSpecialCharErrMsg = () => {
    if (lastNameSpecialCharacterError === "error") {
      return "No special chracters allowed.";
    } else if (lastNameSpecialCharacterError === "") {
      return "";
    }
  };

  const checkForFirstNameSpecialChar = (str: string) => {
    const regex = /^[a-zA-Z\s.'-]+$/;
    return regex.test(str);
  }

  //Add account form inputs
  const handleOnChangeFirstName = (e) => {
    setFirstName(e.target.value);

    if (!checkForFirstNameSpecialChar(e.target.value)) {
      setFirstNameSpecialCharacterError("error");
    } else {
      setFirstNameSpecialCharacterError("");
    }
  };

  const handleOnChangeLastName = (e) => {
    setLastName(e.target.value);

    if (!checkForFirstNameSpecialChar(e.target.value)) {
      setLastNameSpecialCharacterError("error");
    } else if (checkForFirstNameSpecialChar(e.target.value)) {
      setLastNameSpecialCharacterError("");
    }
  };

  const handleOnChangeEmail = (e) => {
    setEmail(e.target.value);
    const emailValid = isValidEmail(e.target.value);
    setEmailIsInvalid(!emailValid);
  };

  const handleOnChangeBusinessName = (e) => {
    setBusinessName(e.target.value);
  };

  const handleOnChangeWebName = (e) => {
    setWebName(e.target.value);
  };

  const handleOnChangeAccountType = (value) => {
    setAccountType(value);
  };

  //Alert account creation status (error / success)
  const renderAccountCreationAlert = () => {
    if (accountCreationStatus === "success") {
      return (
        <Alert
          message="Successfully created account. An activation link has been sent to the account email."
          type={accountCreationStatus}
          style={{ marginTop: "20px" }}
        />
      );
    } else if (accountCreationStatus === "error") {
      return (
        <Alert
          message="Error creating account. Make sure account doesn't exist already and email is valid."
          type={accountCreationStatus}
          style={{ marginTop: "20px" }}
        />
      );
    } else return false;
  };

  return (
    <>
      <Button
        onClick={showAddModal}
        type="primary"
        icon={<PlusOutlined />}
        id="add-account-btn"
      >
        Add Account
      </Button>
      <Modal
        title="Create New Account"
        open={isAddModalVisible}
        onOk={handleAddModalOk}
        onCancel={handleAddModalCancel}
        okType={"primary"}
        okText={"Add"}
        cancelText={"Close"}
        okButtonProps={{ disabled: handleDisableAddBtn() }}
      >
        <Spin spinning={isPendingAdd}>
          <Form layout="vertical">
            <Form.Item
              label="First Name"
              required={true}
              validateStatus={handleFirstNameSpecialCharErr()}
              help={handleFirstNameSpecialCharErrMsg()}
            >
              <Input
                readOnly={isReadOnly}
                placeholder="First Name"
                onChange={handleOnChangeFirstName}
                value={firstName}
              />
            </Form.Item>
            <Form.Item
              label="Last Name"
              required={true}
              validateStatus={handleLastNameSpecialCharErr()}
              help={handleLastNameSpecialCharErrMsg()}
            >
              <Input
                readOnly={isReadOnly}
                placeholder="Last Name"
                onChange={handleOnChangeLastName}
                value={lastName}
              />
            </Form.Item>
            <Form.Item label="Email" required={true}>
              <Input
                readOnly={isReadOnly}
                placeholder="Email"
                onChange={handleOnChangeEmail}
                value={email}
                status={emailIsInvalid ? "error" : ""}
              />
            </Form.Item>
            {emailIsInvalid ? (
              <div
                style={{
                  color: "red",
                  fontSize: "12px",
                  marginTop: "-18px",
                }}
              >
                Please enter a valid email.
              </div>
            ) : null}
            <Form.Item label="Account Type" required={true}>
              <Select
                disabled={isReadOnly}
                defaultValue={accountType}
                onChange={handleOnChangeAccountType}
              >
                <Select.Option value="SPEECH-WORKS">Speech-Works</Select.Option>
                <Select.Option value="ACP">ACP</Select.Option>
              </Select>
            </Form.Item>
            <Form.Item label="Business Name">
              <Input
                readOnly={isReadOnly}
                placeholder="Business Name"
                onChange={handleOnChangeBusinessName}
                value={businessName}
              />
            </Form.Item>
            <Form.Item label="Web Name">
              <Input
                readOnly={isReadOnly}
                placeholder="Web Name"
                onChange={handleOnChangeWebName}
                value={webName}
              />
            </Form.Item>
          </Form>
          <ReCaptcha {...generateReCaptchaProps()} />
          {renderAccountCreationAlert()}
        </Spin>
      </Modal>
    </>
  );
};

export default AccountsOverviewAddAccountModal;


// const checkForSpecialChar = (str: string) => {
//   return !/[~`!#$%\^&*+=\\[\]\\';,/{}|\\":<>\?]/g.test(str);
// };